if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();
      this.deliveryPeriodUnits = this.getParsedDeliveryUnitsMap();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        this.dataset.preorderDeliveryDate,
        this.dataset.preorderCount,
        this.dataset.inventory_quantity_text
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory.preorder_delivery_date,
          inventory.preorder_count,
          inventory.inventory_quantity_text,
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {string} preorderDate - date of the expected preorder delivery
     * @param {string} preorderCount - number of ordered units in preorder
     */
    updateInventory(count, available, inventoryPolicy, preorderDate, preorderCount, countText = '') {
      let countTotalText = count;
      if (countText) {
        countTotalText = countText;
      }
      let inventoryLevel;
      this.showPreorderStatus = this.isPreorderStatusRelevant(count, preorderDate, preorderCount);

      if (count <= 0) {
        if (this.showPreorderStatus) {
          inventoryLevel = 'preordered';
        } else if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'preordered') {
          this.inventoryNotice.innerText = `${this.getPreorderCountText(preorderCount)} ${this.getPreorderDateText(preorderDate)}`;
        } else if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = this.getBackorderedInventoryNoticeText()
            ? `${this.dataset.usualDeliveryPeriodText}${this.getBackorderedInventoryNoticeText()}`
            : this.dataset.inventoryBackordered;
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', countTotalText);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }

    isPreorderStatusRelevant(quantity, date, count) {
      if (quantity > 0) {
        return false;
      }

      return count || date;
    }

    formatDate(dateString) {
      let date = new Date(dateString);
      const currentDate = new Date();

      date.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);
      // If date is already in the past, set the date to current date
      if (currentDate > date) {
        date = currentDate;
      }

      const formatter = new Intl.DateTimeFormat(this.dataset.lang, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return formatter.format(date);
    }

    getPreorderCountText(count) {
      const string = this.dataset.preorderCountText;
      if (!string || !count) {
        return '';
      }

      return string.replace(/\[count]/g, count);
    }

    getPreorderDateText(dateString) {
      const string = this.dataset.preorderDeliveryDateText;
      if (!string || !dateString) {
        return '';
      }

      try {
        const formattedDate = this.formatDate(dateString);
        return string.replace(/\[date]/g, formattedDate);
      } catch (e) {
        console.error(`Unable to transform date: ${dateString}`);
        return '';
      }
    }

    /**
     *
     * Parses the duration string to number and unit
     * @param {string} value - duration string in format <number><unit> [12h, 1d, 2w, 3m]
     * @return {[number, string]}
     */
    parseDuration(value) {
      const match = value.match(/^(\d+)([hdwm])$/);
      if (!match) {
        throw new Error('Invalid duration format');
      }

      const number = parseInt(match[1], 10);
      const unit = match[2];

      const unitMap = {
        h: 'hours',
        d: 'days',
        w: 'weeks',
        m: 'months',
      };

      if (!unitMap[unit]) {
        throw new Error('Invalid unit');
      }

      return [number, unitMap[unit]];
    }

    getParsedDeliveryUnitsMap() {
      try {
        const data = this.dataset.usualDeliveryPeriodUnits;
        if (!data) {
          return null;
        }

        return JSON.parse(data);
      } catch (e) {
        console.error('Unable to parse delivery period units.');
        return null;
      }
    }

    getBackorderedInventoryNoticeText() {
      try {
        const period = this.dataset.usualDeliveryPeriod;
        if (!period) return '';

        const [number, unit] = this.parseDuration(period);
        return `${number} ${this.getCorrectDeliveryUnitTranslation(number, unit)}`;
      } catch (e) {
        return '';
      }
    }

    getCorrectDeliveryUnitTranslation(number, unit) {
      const periodUnit = this.deliveryPeriodUnits[unit];
      if (!periodUnit) {
        console.error(`No translations found for unit: ${unit}`);
        return '';
      }

      try {
        const pluralRules = new Intl.PluralRules(this.dataset.lang);
        const pluralForm = pluralRules.select(number);
        return periodUnit[pluralForm];
      } catch (e) {
        console.error(`Unable to find period unit translation for [number=${number}, unit=${unit}]`);
        return '';
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
